import type * as React from 'react';
import { type FC, useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import type { CrehanaTFunction } from '@crehana/i18n';
import { appendImageCDNToStaticUrl } from '@crehana/next';
import { type DialogStepType, Tour, type TourStepType } from '@crehana/web';
import { useSavedCoursesContext } from '@crehana/web/ReactContexts/SavedCoursesContext';

import { useCrehanaTranslation } from '@/views/UserDashboard/hooks';
import useSavedCoursesTourQuery from '@/views/UserDashboard/hooks/useSavedCoursesTourQuery';
import { SAVED_COURSES_SELECTOR } from '@/views/UserDashboard/routes/selectors';
import type { UserDashboardLocaleKeys } from '@/views/UserDashboard/types';

import CHANGE_ONBOARDING_STATUS_MUTATION from './graphql/ChangeOnboardingStatus.graphql';
import GET_ONBOARDING_STATUS_QUERY from './graphql/GetOnboardingStatus.graphql';
import type {
  ChangeUserDashboardOnboardingStatus as ChangeOnboardingStatusResponse,
  ChangeUserDashboardOnboardingStatusVariables as ChangeOnboardingStatusVariables,
} from './graphql/types/ChangeUserDashboardOnboardingStatus';
import type { GetUserDashboardOnboardingStatus as GetOnboardingStatusResponse } from './graphql/types/GetUserDashboardOnboardingStatus';
import onboardingStep1Image from './images/onboarding-step1.jpg';
import onboardingStep2Image from './images/onboarding-step2.jpg';
import onboardingStep3Image from './images/onboarding-step3.jpg';
import onboardingStep4Image from './images/onboarding-step4.jpg';

const SavedCourseStep = (
  t: CrehanaTFunction<UserDashboardLocaleKeys>,
): TourStepType => ({
  selector: '[data-tour="saved-courses-tab"]',
  title: t('UD_SAVED_COURSES_TITLE'),
  desc: t('UD_SAVED_COURSES_TITLE_DESC'),
  position: 'right',
});

const getSteps = (
  t: CrehanaTFunction<UserDashboardLocaleKeys>,
  ignoreSteps: string[] = [],
): (TourStepType & { selector: string })[] => {
  let steps: (TourStepType & { selector: string })[] = [
    {
      selector: '[data-tour="enrollments-tab"]',
      title: t('UD_ONBOARDING_ENROLLMENTS_TITLE'),
      desc: t('UD_ONBOARDING_ENROLLMENTS_DESC'),
      position: 'right',
    },
  ];

  // Include saved courses step only if available
  if (!ignoreSteps.includes(SAVED_COURSES_SELECTOR)) {
    steps = [...steps, SavedCourseStep(t)];
  }

  steps = [
    ...steps,
    {
      selector: '[data-tour="classnotes-tab"]',
      title: t('UD_ONBOARDING_CLASSNOTES_TITLE'),
      desc: t('UD_ONBOARDING_CLASSNOTES_DESC'),
      position: 'right',
    },
    {
      selector: '[data-tour="projects-tab"]',
      title: t('UD_ONBOARDING_PROJECTS_TITLE'),
      desc: t('UD_ONBOARDING_PROJECTS_DESC'),
      position: 'right',
    },
    {
      selector: '[data-tour="diplomas-tab"]',
      title: t('UD_ONBOARDING_DIPLOMAS_TITLE'),
      desc: t('UD_ONBOARDING_DIPLOMAS_DESC'),
      position: 'right',
    },
    {
      selector: '[data-tour="study-plan-tab"]',
      title: t('UD_ONBOARDING_STUDY_PLAN_TITLE'),
      desc: t('UD_ONBOARDING_STUDY_PLAN_DESC'),
      position: 'right',
    },
    {
      selector: '[data-tour="dashboard-night-switch"]',
      title: t('UD_ONBOARDING_NIGHT_SWITCH_TITLE'),
      desc: t('UD_ONBOARDING_NIGHT_SWITCH_DESC'),
      position: 'right',
    },
    {
      selector: '[data-tour="sidebar-config"]',
      title: t('UD_ONBOARDING_CONFIG_TITLE'),
      desc: t('UD_ONBOARDING_CONFIG_DESC'),
      position: 'right',
    },
    {
      selector: '[data-tour="sidebar-faq"]',
      title: t('UD_ONBOARDING_FAQ_TITLE'),
      desc: t('UD_ONBOARDING_FAQ_DESC'),
      position: 'right',
    },
    {
      selector: '[data-tour="dashboard-header-options"]',
      title: t('UD_ONBOARDING_SEARCH_TITLE'),
      desc: t('UD_ONBOARDING_SEARCH_DESC'),
      position: 'bottom',
    },
  ];

  return steps;
};

const getMobileSteps = (
  t: CrehanaTFunction<UserDashboardLocaleKeys>,
): DialogStepType[] => [
  {
    title: t('UD_ONBOARDING_MOBILE_MENU_TITLE'),
    desc: t('UD_ONBOARDING_MOBILE_MENU_DESC'),
    img: appendImageCDNToStaticUrl(onboardingStep1Image.src),
  },
  {
    title: t('UD_ONBOARDING_NIGHT_SWITCH_TITLE'),
    desc: t('UD_ONBOARDING_NIGHT_SWITCH_DESC'),
    img: appendImageCDNToStaticUrl(onboardingStep2Image.src),
  },
  {
    title: t('UD_ONBOARDING_FAQ_TITLE'),
    desc: t('UD_ONBOARDING_FAQ_DESC'),
    img: appendImageCDNToStaticUrl(onboardingStep3Image.src),
  },
  {
    title: t('UD_ONBOARDING_SEARCH_TITLE'),
    desc: t('UD_ONBOARDING_SEARCH_DESC'),
    img: appendImageCDNToStaticUrl(onboardingStep4Image.src),
  },
];

const ONBOARDING_STATUS = {
  INCOMPLETE: 'incomplete',
  SKIPPED: 'ignore',
  COMPLETE: 'completed',
};

interface OnboardingProps {
  tourIsOpen: boolean;
  toggleTour: () => void;
  tourIsLoading?: (loading: boolean) => void;
  validateSavedCoursesTour?: () => void;
}

const Onboarding: FC<React.PropsWithChildren<OnboardingProps>> = ({
  tourIsOpen,
  toggleTour,
  tourIsLoading,
  validateSavedCoursesTour,
}) => {
  const t = useCrehanaTranslation();

  const { loading, data, error, refetch } = useQuery<
    GetOnboardingStatusResponse,
    // biome-ignore lint/complexity/noBannedTypes: type usage acceptable in this context
    {}
  >(GET_ONBOARDING_STATUS_QUERY);
  const [queryRetries, setQueryRetries] = useState<number>(0);
  const [changeOnboardingStatus] = useMutation<
    ChangeOnboardingStatusResponse,
    ChangeOnboardingStatusVariables
  >(CHANGE_ONBOARDING_STATUS_MUTATION);
  const { isEnabledSaveCourses } = useSavedCoursesContext();

  const STEPS = getSteps(
    t,
    isEnabledSaveCourses ? [] : [SAVED_COURSES_SELECTOR],
  );
  const MOBILE_STEPS = getMobileSteps(t);
  const {
    onComplete: onCompleteSavedCoursesTour,
    onSkip: onSkipSavedCoursesTour,
  } = useSavedCoursesTourQuery();

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  useEffect(() => {
    tourIsLoading?.(loading);
  }, [loading]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  useEffect(() => {
    if (!loading && (error || !data?.me) && queryRetries < 3) {
      setQueryRetries(queryRetries + 1);
      refetch();
    } else if (
      data?.me?.tourOnboardingCompleted === ONBOARDING_STATUS.INCOMPLETE &&
      !tourIsOpen
    ) {
      toggleTour();
    } else {
      validateSavedCoursesTour?.();
    }
  }, [loading, data, error]);

  const onSkip = () => {
    toggleTour();
    if (data?.me?.tourOnboardingCompleted !== ONBOARDING_STATUS.SKIPPED) {
      changeOnboardingStatus({
        variables: { status: ONBOARDING_STATUS.SKIPPED },
      });
    }
    onSkipSavedCoursesTour();
  };

  const onComplete = () => {
    toggleTour();
    if (data?.me?.tourOnboardingCompleted !== ONBOARDING_STATUS.COMPLETE) {
      changeOnboardingStatus({
        variables: { status: ONBOARDING_STATUS.COMPLETE },
      });
    }

    onCompleteSavedCoursesTour();
  };

  return (
    <Tour
      type="default"
      open={tourIsOpen}
      welcomeType="dashboard"
      welcomeTitle={t('UD_ONBOARDING_WELCOME_TITLE')}
      welcomeDesc={t('UD_ONBOARDING_WELCOME_DESC')}
      steps={STEPS}
      mobileSteps={MOBILE_STEPS}
      onSkip={onSkip}
      onComplete={onComplete}
      breakpoint="sm"
    />
  );
};

export default Onboarding;
